// Grids & Gantts — the structure engine behind the WIP page.
//
// A structure is not a list. It assembles itself from GENERATORS, and this
// file is the whole of that assembly, with no network and no React in it —
// which is what lets it run under node in its own suite and be reused by
// every grid on every site.
//
// The pieces, in the order a structure uses them:
//
//   insert    a query's answers become the folder's top-level rows
//   extend    each row finds what hangs under it: the Parent field, and/or
//             work items named by a link type's relation ("Link to Child",
//             "Link to Oven"), as deep as the data and maxDepth allow
//   filter    a query every level is passed through, structure-wide and
//             per folder — an item the filter drops is not carried, and
//             nothing under it is reached through it
//   group     a field that brackets the folder's top-level rows
//   sort      the order rows take, once at the top and once under a parent
//
// TWO LOADERS, ONE ENGINE. `memorySource` assembles the tree from one broad
// load already in hand; `querySource` walks level by level, a query per
// level. The engine cannot tell them apart, so the tests exercise the same
// code the browser runs, and a page can switch loaders without changing a
// line of this.
//
// THE TWO COUNTS ARE DIFFERENT QUESTIONS, deliberately:
//   Count            distinct WORK ITEMS in the branch, this row included.
//   Count Sub-Items  ROWS below this one. An item named by two parents is
//                    two rows and counts twice; a GROUP HEADER is a row too.
// That is why a folder shows fewer in Count than in Count Sub-Items: the
// difference is the work sitting in more than one place, plus the headers
// the groupings draw. Both halves were checked against the structure this
// page replaces, group by group.

export const CHUNK = 50;

export const keyOf = (it) => (it && it.key) || '';
export const fieldsOf = (it) => (it && it.fields) || {};
export const parentKeyOf = (it) => (fieldsOf(it).parent || {}).key || '';

// ---- the link extender -------------------------------------------------------
// The link sits on the PARENT with the child as its inward issue and the
// type's inward wording reading "Link to Child". The outward side is
// checked too, so a site that words the relation the other way round still
// finds the same children.
export function linkedChildKeys(issue, links = [], depth = 1) {
  const out = [];
  for (const rule of links || []) {
    if (!rule || !rule.relation) continue;
    const from = rule.levelFrom == null ? 1 : Number(rule.levelFrom);
    const to = rule.levelTo == null ? 99 : Number(rule.levelTo);
    if (depth < from || depth > to) continue;
    const wantType = String(rule.type || '').trim().toLowerCase();
    const rel = String(rule.relation).trim().toLowerCase();
    for (const l of (fieldsOf(issue).issuelinks || [])) {
      const t = l && l.type;
      if (!t) continue;
      if (wantType && String(t.name || '').trim().toLowerCase() !== wantType) continue;
      const inward = l.inwardIssue && l.inwardIssue.key;
      const outward = l.outwardIssue && l.outwardIssue.key;
      if (inward && String(t.inward || '').trim().toLowerCase() === rel) out.push(inward);
      else if (outward && String(t.outward || '').trim().toLowerCase() === rel) out.push(outward);
    }
  }
  return out;
}

// ---- sorting -----------------------------------------------------------------
// Keys sort the way a person reads them: FYP2-2 before FYP2-11.
const keyCmp = (a, b) => String(keyOf(a)).localeCompare(String(keyOf(b)), undefined, { numeric: true, sensitivity: 'base' });

export function valueFor(it, by, startFieldId = '') {
  const f = fieldsOf(it);
  if (by === 'duedate') return String(f.duedate || '').slice(0, 10);
  if (by === 'start') return String((startFieldId && f[startFieldId]) || '').slice(0, 10);
  if (by === 'summary') return String(f.summary || '');
  if (by === 'status') return String((f.status && f.status.name) || '');
  if (by === 'type') return String((f.issuetype && f.issuetype.name) || '');
  // ANY FIELD, by this site's id ('fieldid:customfield_10205' — the page
  // turns the config's `field:<name>` into this once it knows the site). A
  // select reads as its value, a user as their name, a list as its first.
  if (typeof by === 'string' && by.startsWith(FIELD_ID_SORT)) return fieldText(f[by.slice(FIELD_ID_SORT.length)]);
  return keyOf(it);
}
export const FIELD_ID_SORT = 'fieldid:';
const fieldText = (v) => {
  const x = Array.isArray(v) ? v[0] : v;
  if (x == null) return '';
  if (typeof x === 'object') return String(x.value || x.displayName || x.name || x.key || '');
  return String(x);
};
const NUMERIC = /^-?\d+(\.\d+)?$/;

// A missing date goes last — an undated item is not the most urgent thing
// on the page. 'none' keeps the order the insert query gave.
export function sortItems(list, by, startFieldId = '') {
  if (!by || by === 'none') return (list || []).slice();
  const byField = typeof by === 'string' && by.startsWith(FIELD_ID_SORT);
  // A field sort, like a date sort, puts the empty values last.
  const dated = by === 'duedate' || by === 'start' || byField;
  return (list || []).slice().sort((a, b) => {
    const av = valueFor(a, by, startFieldId);
    const bv = valueFor(b, by, startFieldId);
    if (byField && NUMERIC.test(av) && NUMERIC.test(bv)) return (Number(av) - Number(bv)) || keyCmp(a, b);
    if (dated) {
      if (!av && !bv) return keyCmp(a, b);
      if (!av) return 1;
      if (!bv) return -1;
    }
    return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) || keyCmp(a, b);
  });
}

// ---- the two sources ---------------------------------------------------------
// Both answer the same two questions. Neither knows what it is being used
// for, which is the point.

// One broad load already in hand. The Parent extender becomes a map lookup
// and the link extender a key lookup — no further network, however deep.
export function memorySource(items) {
  const byKey = new Map();
  const kidsByParent = new Map();
  (items || []).forEach((it) => {
    const k = keyOf(it);
    if (!k || byKey.has(k)) return;
    byKey.set(k, it);
  });
  byKey.forEach((it, k) => {
    const pk = parentKeyOf(it);
    if (!pk) return;
    const l = kidsByParent.get(pk) || [];
    l.push(k);
    kidsByParent.set(pk, l);
  });
  return {
    kind: 'memory',
    size: byKey.size,
    async childrenOfParents(keys) {
      const out = [];
      (keys || []).forEach((k) => (kidsByParent.get(k) || []).forEach((ck) => {
        const c = byKey.get(ck);
        if (c) out.push(c);
      }));
      return out;
    },
    async byKeys(keys) {
      const out = [];
      (keys || []).forEach((k) => { const c = byKey.get(k); if (c) out.push(c); });
      return out;
    },
  };
}

// A query per level. `run(zql)` returns the rows; the filter is folded into
// every query so a level never carries what the structure filter drops.
//
// THE CHUNKS OF A LEVEL GO OUT IN PARALLEL, and that is not a micro-
// optimisation. A level of fifteen thousand parents is fifty queries at
// `chunk` 300; run one after another against a round trip of a second or
// two, that level alone is a minute and a half of somebody watching a
// progress bar. They do not depend on each other — every chunk asks about a
// different set of parents — so there is no reason but habit to wait for
// one before sending the next. `parallel` is deliberately a small number:
// the point is to hide latency, not to flood the server that is also
// serving everyone else.
// ---- keeping N requests in flight ---------------------------------------------
// A POOL, NOT A BATCH. This used to send `parallel` queries, WAIT FOR ALL OF
// THEM, then send the next `parallel` — so one slow query left the other four
// slots idle until it came back, every round. Over fifty rounds that is most
// of a minute spent waiting for nothing. A pool starts the next query the
// moment any slot frees, which is the same load on the server and a much
// shorter wall clock.
//
// It is handed IN so several folders can share one, and `walkParallel` keeps
// meaning what it says: how many queries this page has in flight AT ALL.
export function makePool(width) {
  const w = Math.max(1, Number(width) || 1);
  let active = 0;
  const waiting = [];
  const pump = () => {
    while (active < w && waiting.length) {
      const job = waiting.shift();
      active += 1;
      Promise.resolve()
        .then(job.fn)
        .then(job.resolve, job.reject)
        .then(() => { active -= 1; pump(); });
    }
  };
  return (fn) => new Promise((resolve, reject) => { waiting.push({ fn, resolve, reject }); pump(); });
}

// ---- asking about many keys at once -------------------------------------------
// THE QUICK FILTERS WERE ASKED TWO HUNDRED KEYS AT A TIME, ONE QUERY AFTER
// ANOTHER. Over a structure of twenty-nine thousand work items that is a
// hundred and forty-five round trips in a row — the better part of two
// minutes, during which the grid showed three folder rows and nothing else.
// "Overdue" read as a button that emptied the page.
//
// The ZQL goes by POST, so a thousand keys is not a long URL; and the chunks
// do not depend on each other, so they go through a pool like the walk does.
// Twenty-nine queries, four at a time, is seconds.
export const FILTER_CHUNK = 1000;
export const FILTER_PARALLEL = 4;

export function chunkKeys(keys, size) {
  const n = Math.max(1, Number(size) || 1);
  const list = keys || [];
  const out = [];
  for (let i = 0; i < list.length; i += n) out.push(list.slice(i, i + n));
  return out;
}

// ---- asking for the same work item once ---------------------------------------
// THE FOLDERS OVERLAP, AND THEY USED TO PAY FOR IT SEPARATELY. Three folders
// that all insert Projects then all walk those projects' children: the second
// and third asked the server for children this page already had in memory.
//
// So a load carries one cache, and a source consults it before asking:
//
//   items    key → the work item, or null for one the FILTER dropped. The
//            null matters: without it a filtered-out key is re-asked for by
//            every folder that links to it, forever.
//   parents  parent key → its children. A parent with none is remembered as
//            an empty list, for the same reason.
//
// It is keyed by the FILTER, because two folders reading through different
// filters are not asking the same question and their answers are not
// interchangeable.
export function makeLoadCache() {
  return { byFilter: new Map(), queries: 0, saved: 0 };
}

const bucketOfCache = (cache, filterZql) => {
  const key = String(filterZql || '').trim();
  if (!cache.byFilter.has(key)) cache.byFilter.set(key, { items: new Map(), parents: new Map() });
  return cache.byFilter.get(key);
};

export function querySource(run, { chunk = CHUNK, filterZql = '', parallel = 5, pool = null, cache = null } = {}) {
  const and = (q) => (filterZql && filterZql.trim() ? `(${q}) AND (${filterZql.trim()})` : q);
  const own = cache || makeLoadCache();
  const bucket = bucketOfCache(own, filterZql);
  const limit = pool || makePool(parallel);

  const ask = async (keys, make) => {
    const queries = [];
    for (let i = 0; i < keys.length; i += chunk) queries.push(make(keys.slice(i, i + chunk)));
    own.queries += queries.length;
    const rows = await Promise.all(queries.map((q) => limit(() => run(and(q)))));
    const out = [];
    rows.forEach((batch) => (batch || []).forEach((r) => out.push(r)));
    return out;
  };

  const uniq = (keys) => [...new Set((keys || []).filter(Boolean))];

  return {
    kind: 'query',
    async childrenOfParents(keys) {
      const list = uniq(keys);
      const unknown = list.filter((k) => !bucket.parents.has(k));
      own.saved += list.length - unknown.length;
      if (unknown.length) {
        const rows = await ask(unknown, (c) => `parent in (${c.join(', ')})`);
        // Seed every parent we asked about FIRST, so a childless one is
        // remembered as childless rather than asked about again.
        unknown.forEach((k) => bucket.parents.set(k, []));
        rows.forEach((r) => {
          const pk = parentKeyOf(r);
          if (!pk) return;
          if (!bucket.parents.has(pk)) bucket.parents.set(pk, []);
          bucket.parents.get(pk).push(r);
          bucket.items.set(keyOf(r), r);
        });
      }
      const out = [];
      list.forEach((k) => (bucket.parents.get(k) || []).forEach((r) => out.push(r)));
      return out;
    },
    async byKeys(keys) {
      const list = uniq(keys);
      const unknown = list.filter((k) => !bucket.items.has(k));
      own.saved += list.length - unknown.length;
      if (unknown.length) {
        const rows = await ask(unknown, (c) => `key in (${c.join(', ')})`);
        rows.forEach((r) => bucket.items.set(keyOf(r), r));
        // A key the filter dropped is remembered as dropped.
        unknown.forEach((k) => { if (!bucket.items.has(k)) bucket.items.set(k, null); });
      }
      return list.map((k) => bucket.items.get(k)).filter(Boolean);
    },
  };
}

// ---- the expansion -----------------------------------------------------------
// Level by level, because that is the only shape whose cost follows the
// DEPTH of the tree rather than its size — true of both sources.
export async function expandFolder({
  roots, source, byParent = true, links = [], maxDepth = 8, maxRows = 60000,
  keep, onProgress,
}) {
  const byKey = new Map();
  const kidsOf = new Map();
  const ok = (it) => (typeof keep === 'function' ? !!keep(it) : true);
  const add = (it) => {
    const k = keyOf(it);
    if (!k || byKey.has(k) || !ok(it)) return false;
    byKey.set(k, it);
    return true;
  };
  // A SET BESIDE THE LIST. The list is the ORDER the children were found in,
  // which the tree needs; the set is "have I seen this one", which `includes`
  // was answering by walking the list again for every child. A parent with a
  // thousand children cost half a million comparisons to build, and a folder
  // full of such parents is most of the time between the last response
  // arriving and the first row appearing.
  const kidSeen = new Map();
  const join = (pk, ck) => {
    if (!pk || !ck || pk === ck) return;
    let seen = kidSeen.get(pk);
    if (!seen) { seen = new Set(); kidSeen.set(pk, seen); kidsOf.set(pk, kidsOf.get(pk) || []); }
    if (seen.has(ck)) return;
    seen.add(ck);
    kidsOf.get(pk).push(ck);
  };

  const rootList = (roots || []).filter(ok);
  rootList.forEach(add);
  let frontier = rootList.slice();
  let depth = 0;

  while (frontier.length && depth < maxDepth && byKey.size < maxRows) {
    depth += 1;
    const found = [];

    // THE TWO HALVES OF A LEVEL GO AT THE SAME TIME.
    //
    // "Children by the Parent field" and "children by a link type" both read
    // the SAME frontier and neither can see what the other found (nothing is
    // added to `byKey` until the level ends), so waiting for the first before
    // sending the second bought nothing and cost a full round trip per level.
    // On a folder that uses both — which is the one that takes the longest —
    // that is half the wall clock, for free.
    const wanted = new Map();                      // child key → the parents naming it
    if ((links || []).length) {
      for (const it of frontier) {
        for (const ck of linkedChildKeys(it, links, depth)) {
          if (!wanted.has(ck)) wanted.set(ck, []);
          wanted.get(ck).push(keyOf(it));
        }
      }
    }
    const need = wanted.size ? [...wanted.keys()].filter((k) => !byKey.has(k)) : [];

    /* eslint-disable no-await-in-loop */
    const [parentRows, linkRows] = await Promise.all([
      byParent ? source.childrenOfParents(frontier.map(keyOf).filter(Boolean)) : Promise.resolve([]),
      need.length ? source.byKeys(need) : Promise.resolve([]),
    ]);
    /* eslint-enable no-await-in-loop */

    if (byParent) {
      for (const c of parentRows || []) {
        if (!ok(c)) continue;
        const pk = parentKeyOf(c);
        if (!pk) continue;
        join(pk, keyOf(c));
        found.push(c);
      }
    }

    if (wanted.size) {
      const got = new Map();
      (linkRows || []).forEach((r) => { if (ok(r)) got.set(keyOf(r), r); });
      for (const [ck, parents] of wanted) {
        // Not found means the filter dropped it (ARCHIVE, TRASH) — the
        // structure does not carry it, and neither does this.
        const c = got.get(ck) || byKey.get(ck);
        if (!c) continue;
        parents.forEach((pk) => join(pk, ck));
        found.push(c);
      }
    }

    const fresh = found.filter((c) => add(c));
    if (onProgress) onProgress({ depth, found: found.length, fresh: fresh.length, total: byKey.size });
    frontier = fresh;
  }

  return { roots: rootList, byKey, kidsOf, depth };
}

// Everything under a key, once each, however many ways it is reached.
export function descendants(key, kidsOf, byKey, seen = new Set(), out = []) {
  for (const ck of (kidsOf.get(key) || [])) {
    if (seen.has(ck)) continue;
    seen.add(ck);
    const c = byKey.get(ck);
    if (c) out.push(c);
    descendants(ck, kidsOf, byKey, seen, out);
  }
  return out;
}

// ---- the two counts ----------------------------------------------------------
// One walk answers both, memoised per key, so a 30 000-row structure costs
// one pass and not one pass per row.
//
//   keys  the DISTINCT work items at and below this key  → Count
//   rows  the row instances BELOW this key               → Count Sub-Items
//
// A cycle contributes nothing rather than looping: an item already on the
// path is not counted again through itself.
export function makeCounter(byKey, kidsOf) {
  const memo = new Map();
  const onPath = new Set();
  const at = (key) => {
    if (memo.has(key)) return memo.get(key);
    if (onPath.has(key)) return { keys: new Set(), rows: 0 };
    onPath.add(key);
    const keys = new Set([key]);
    let rows = 0;
    for (const ck of (kidsOf.get(key) || [])) {
      if (!byKey.has(ck)) continue;
      const sub = at(ck);
      rows += 1 + sub.rows;
      sub.keys.forEach((k) => keys.add(k));
    }
    onPath.delete(key);
    const out = { keys, rows };
    memo.set(key, out);
    return out;
  };
  return {
    at,
    // A folder or a group: the union over what it holds, itself not counted.
    over(keyList) {
      const keys = new Set();
      let rows = 0;
      (keyList || []).forEach((k) => {
        if (!byKey.has(k)) return;
        const sub = at(k);
        rows += 1 + sub.rows;
        sub.keys.forEach((x) => keys.add(x));
      });
      return { keys, rows };
    },
  };
}

// WHAT A GROUPING ANSWERS. A grouping used to answer a name; it answers
// { key, label, order } now (see grouping.js) — the key is what the rows
// are bracketed by and what the path carries, the label is what the header
// says, and the order, when there is one, is how headers sort (a date's
// day number, a bucket's position); without one they sort by label. A plain
// string still works, as key and label both.
export function normGroup(v) {
  if (v && typeof v === 'object') {
    const key = String(v.key != null ? v.key : (v.label != null ? v.label : '(none)')) || '(none)';
    const label = String(v.label != null ? v.label : key) || '(none)';
    const g = { key, label, order: typeof v.order === 'number' ? v.order : null };
    // A header may wear a small picture (a project's avatar), and a tag
    // beside its label ("This Month" on the period that holds today).
    if (typeof v.icon === 'string' && v.icon) g.icon = v.icon;
    if (typeof v.tag === 'string' && v.tag) g.tag = v.tag;
    // ...or the look of what it groups by: a work type's icon, a status's
    // lozenge, drawn from one work item of the group (`sample`).
    if ((v.look === 'type' || v.look === 'status') && v.sample) { g.look = v.look; g.sample = v.sample; }
    return g;
  }
  const s = String(v == null || v === '' ? '(none)' : v);
  return { key: s, label: s, order: null };
}
const cmpText = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
export const cmpGroups = (a, b) => ((a.order != null && b.order != null) ? (a.order - b.order || cmpText(a.key, b.key)) : cmpText(a.label, b.label));
// Bracket items by a grouping, in header order.
export function bracket(items, groupsOf, depth) {
  const by = new Map();
  items.forEach((it) => {
    const g = normGroup(groupsOf(it, depth));
    if (!by.has(g.key)) by.set(g.key, { ...g, items: [] });
    by.get(g.key).items.push(it);
  });
  return [...by.values()].sort(cmpGroups);
}

// How many GROUP rows a folder's groupings put below a given set of items.
//
// Count Sub-Items counts ROWS, and a group header is a row. Checked against
// the structure this page replaces, group by group: its "Admin, Management
// & HR" shows 5 work items and 7 sub-items, and the two extra are the two
// Status groups inside it; "Investment Projects" shows 16 and 18 for the
// same reason. Counting only work items makes every grouped folder read
// short by exactly the number of headers it draws.
export function countGroupRows(items, folder, depth, groupsOf) {
  const groups = (folder && (folder.groups)) || [];
  if (depth >= groups.length || !items.length) return 0;
  const groups2 = bracket(items, groupsOf, depth);
  let n = groups2.length;
  groups2.forEach((g) => { n += countGroupRows(g.items, folder, depth + 1, groupsOf); });
  return n;
}

// GROUP THE CHILDREN (a folder's groupChildren rule): part.childGroup =
// { of(item) } answers the header a CHILD goes under — a value, or the
// { key, label, order } a grouping answers — or null for a child that is not
// grouped (another work type). Under every row, the children not grouped come
// first, then one header per value, each with its children under it. The
// folder's own groupings (above) still bracket only what the folder inserts.
export function splitChildren(kids, childGroup) {
  if (!childGroup || typeof childGroup.of !== 'function' || !kids.length) return { rest: kids, groups: [] };
  const rest = []; const grouped = [];
  for (const c of kids) { const v = childGroup.of(c); (v == null ? rest : grouped).push(c); }
  if (!grouped.length) return { rest: kids, groups: [] };
  return { rest, groups: bracket(grouped, (it) => childGroup.of(it), 0) };
}
// How many child-group HEADER rows sit in a row's whole subtree (Count
// Sub-Items counts rows, and a header is a row) — one function per part.
function childHeaderCounter(byKey, kidsOf, childGroup) {
  if (!childGroup) return () => 0;
  const memo = new Map();
  const at = (k, onPath) => {
    if (memo.has(k)) return memo.get(k);
    if (onPath.has(k)) return 0;
    onPath.add(k);
    const kids = (kidsOf.get(k) || []).filter((ck) => byKey.has(ck)).map((ck) => byKey.get(ck));
    let n = splitChildren(kids, childGroup).groups.length;
    kids.forEach((c) => { n += at(keyOf(c), onPath); });
    onPath.delete(k);
    memo.set(k, n);
    return n;
  };
  return (k) => at(k, new Set());
}

// ---- the flat rows the grid draws --------------------------------------------
// One row per visible node, carrying its level, so the grid indents the way
// a structure does and a shut row takes its whole subtree with it.
//
// `parts` is one entry per folder:
//   { folder, roots, byKey, kidsOf, counter, groupsOf, linesOf? }
// `groupsOf(item)` returns the group names for that item, outermost first.
// `linesOf(key)`, when a folder draws TABLE LINES (./lines.js), returns a
// work item's lines: they are drawn first under it, one LINE ROW each — not
// work items, so they are in no count, no filter and no path to open.
const NO_KEYS = new Set();
export function flattenStructure({ parts, collapsed, sortRoot = 'duedate', startFieldId = '', rules = null }) {
  const out = [];
  const shut = (path) => !!(collapsed && collapsed.has(path));

  // cg: { childGroup, headers(k) } when the folder groups children (else null)
  const walkItems = (items, byKey, kidsOf, counter, level, parentPath, sortChild, onPath, linesOf = null, cg = null) => {
    for (const it of items) {
      const k = keyOf(it);
      const path = `${parentPath}/${k}`;
      const cyclic = onPath.has(k);
      const kidKeys = cyclic ? [] : (kidsOf.get(k) || []).filter((ck) => byKey.has(ck));
      const lines = (!cyclic && linesOf && linesOf(k)) || [];
      const hasKids = kidKeys.length > 0 || lines.length > 0;
      const open = hasKids && !shut(path);
      const stat = counter.at(k);
      out.push({
        kind: 'item', isGroup: false, it, key: k, path, rowId: path, level, cyclic,
        hasKids, kidCount: kidKeys.length + lines.length, open,
        countKeys: stat.keys, count: stat.keys.size, subRows: stat.rows + (cg ? cg.headers(k) : 0),
      });
      if (open) {
        lines.forEach((ln) => {
          const lp = `${path}/~${ln.line.n}`;
          out.push({
            kind: 'line', isGroup: false, it: null, line: ln, key: ln.key, path: lp, rowId: lp, level: level + 1,
            summary: (ln.fields && ln.fields.summary) || '', hasKids: false, kidCount: 0, open: false,
            countKeys: NO_KEYS, count: 0, subRows: 0,
          });
        });
        onPath.add(k);
        const kids = sortItems(kidKeys.map((ck) => byKey.get(ck)), sortChild, startFieldId);
        const { rest, groups } = splitChildren(kids, cg && cg.childGroup);
        walkItems(rest, byKey, kidsOf, counter, level + 1, path, sortChild, onPath, linesOf, cg);
        // the grouped children: a header per value, its children under it
        groups.forEach((g) => {
          const gp = `${path}/c:${g.key}`;
          const gOpen = !shut(gp);
          const gs = counter.over(g.items.map(keyOf));
          out.push({
            kind: 'group', isGroup: true, isChildGroup: true, groupField: (cg.childGroup && cg.childGroup.field) || '', key: gp, path: gp, rowId: gp,
            level: level + 1, summary: g.label, icon: g.icon || '', tag: g.tag || '', look: g.look || '', sample: g.sample || null,
            hasKids: true, kidCount: g.items.length, open: gOpen,
            countKeys: gs.keys, count: gs.keys.size, subRows: gs.rows + g.items.reduce((n, x) => n + cg.headers(keyOf(x)), 0),
          });
          if (gOpen) walkItems(g.items, byKey, kidsOf, counter, level + 2, gp, sortChild, onPath, linesOf, cg);
        });
        onPath.delete(k);
      }
    }
  };
  // a part's child grouping, with its header counter (made once per part)
  const cgMemo = new Map();
  const cgOf = (part) => {
    if (!part.childGroup) return null;
    if (!cgMemo.has(part)) cgMemo.set(part, { childGroup: part.childGroup, headers: childHeaderCounter(part.byKey, part.kidsOf, part.childGroup) });
    return cgMemo.get(part);
  };

  // A folder's own groupings, outermost first, then the items.
  // THE GROUPINGS A PART BRACKETS BY are part.groups when the page gives
  // them (an unset grouping is skipped) and the folder's own otherwise. The
  // folder itself stays whole, because it is also what the amber rules are
  // printed from — an unset grouping must still print as "(no field yet)".
  const groupsFor = (part) => part.groups || part.folder.groups || [];
  const walkGroups = (items, part, depth, level, parentPath) => {
    const { folder, byKey, kidsOf, counter, groupsOf } = part;
    const groups = groupsFor(part);
    if (depth >= groups.length) {
      walkItems(sortItems(items, folder.sort, startFieldId), byKey, kidsOf, counter, level, parentPath, folder.sort, new Set(), part.linesOf || null, cgOf(part));
      return;
    }
    const cg = cgOf(part);
    bracket(items, groupsOf, depth).forEach((g) => {
      const group = g.items;
      const path = `${parentPath}/g:${groups[depth].id}:${g.key}`;
      const open = !shut(path);
      const stat = counter.over(group.map(keyOf));
      out.push({
        kind: 'group', isGroup: true, groupField: groups[depth].field || groups[depth].kind, key: path, path, rowId: path,
        level, summary: g.label, icon: g.icon || '', tag: g.tag || '', look: g.look || '', sample: g.sample || null, hasKids: true, kidCount: group.length, open,
        countKeys: stat.keys, count: stat.keys.size,
        subRows: stat.rows + countGroupRows(group, { groups }, depth + 1, groupsOf) + (cg ? group.reduce((n, x) => n + cg.headers(keyOf(x)), 0) : 0),
      });
      if (open) walkGroups(group, part, depth + 1, level + 1, path);
    });
  };

  // The structure's own rules, printed the way a structure prints them.
  if (rules && rules.top && rules.top.length) {
    rules.top.forEach((r, i) => out.push({ kind: 'rule', isRule: true, key: `rule/top/${i}`, path: `rule/top/${i}`, rowId: `rule/top/${i}`, level: 0, rule: r }));
  }

  // FOLDERS NEST. A folder may sit inside another (folder.parentId): its row
  // is drawn one level in, its own rules and the folders inside it come
  // first, then its own rows — and the parent's two counts take in
  // everything the folders inside it hold (Count over the distinct work
  // items, Count Sub-Items over the rows, a sub-folder's row included).
  const tree = folderTree(parts);
  const statMemo = new Map();
  const folderStat = (part) => {
    const id = part.folder.id;
    if (statMemo.has(id)) return statMemo.get(id);
    const roots = sortItems(part.roots || [], sortRoot, startFieldId);
    const own = part.counter.over(roots.map(keyOf));
    const keys = new Set(own.keys);
    const cg = cgOf(part);
    let rows = own.rows + countGroupRows(roots, { groups: groupsFor(part) }, 0, part.groupsOf) + (cg ? roots.reduce((n, x) => n + cg.headers(keyOf(x)), 0) : 0);
    (tree.childrenOf.get(id) || []).forEach((child) => {
      const s = folderStat(child);
      s.keys.forEach((k) => keys.add(k));
      rows += 1 + s.rows;
    });
    const stat = { keys, rows, roots };
    statMemo.set(id, stat);
    return stat;
  };
  const walkFolder = (part, level, parentPath) => {
    const { folder } = part;
    const path = parentPath ? `${parentPath}/f:${folder.id}` : `f:${folder.id}`;
    const stat = folderStat(part);
    const subs = tree.childrenOf.get(folder.id) || [];
    // A BARE FOLDER has no row: what it holds sits at its level, always open.
    // With the rules on (admin mode) it keeps a row, so its rules have
    // somewhere to print and someone can edit them.
    if (folder.bare && !(rules && rules.folder)) {
      subs.forEach((child) => walkFolder(child, level, path));
      walkGroups(stat.roots, part, 0, level, path);
      return;
    }
    const open = !shut(path);
    out.push({
      kind: 'folder', isGroup: true, isFolder: true, folder, key: path, path, rowId: path, level,
      summary: folder.bare ? `${folder.name} (bare — no row for readers)` : folder.name, icon: folder.icon, hasKids: stat.roots.length > 0 || subs.length > 0, kidCount: stat.roots.length + subs.length, open,
      countKeys: stat.keys, count: stat.keys.size, subRows: stat.rows,
    });
    if (!open) return;
    if (rules && rules.folder) {
      (rules.folder(folder) || []).forEach((r, i) => out.push({
        kind: 'rule', isRule: true, key: `rule/${folder.id}/${i}`, path: `rule/${folder.id}/${i}`,
        rowId: `rule/${folder.id}/${i}`, level: level + 1, rule: r,
      }));
    }
    subs.forEach((child) => walkFolder(child, level + 1, path));
    walkGroups(stat.roots, part, 0, level + 1, path);
  };
  tree.roots.forEach((part) => walkFolder(part, 0, ''));

  return out;
}

// The folders as a tree: which parts sit at the top and which inside which.
// A parent that is not in the structure, a folder naming itself, or a cycle
// counts as the top — the config normaliser fixes those too, but the
// engine never trusts it to have run.
export function folderTree(parts) {
  const list = parts || [];
  const byId = new Map(list.map((p) => [p.folder.id, p]));
  const parentOf = (p) => {
    const pid = p.folder.parentId;
    if (!pid || pid === p.folder.id || !byId.has(pid)) return null;
    const seen = new Set([p.folder.id]);
    let cur = byId.get(pid);
    while (cur) {
      if (seen.has(cur.folder.id)) return null;      // a cycle: this one goes to the top
      seen.add(cur.folder.id);
      const next = cur.folder.parentId;
      cur = next && next !== cur.folder.id && byId.has(next) ? byId.get(next) : null;
    }
    return pid;
  };
  const childrenOf = new Map();
  const roots = [];
  list.forEach((p) => {
    const pid = parentOf(p);
    if (pid === null) { roots.push(p); return; }
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid).push(p);
  });
  return { roots, childrenOf, byId };
}

// Every path in the tree with the level it sits at — what "Expand all" has
// to know, what "Collapse all" has to fill, and how the page opens the tree
// to a given depth when it lands. `rel` is the level counted from the
// folder the row is in (a folder row is 0, its own rows 1, …), which is what
// "open N levels when the page lands" means whether or not the folder itself
// sits inside another; `isFolder` marks the folder rows.
export function allPaths({ parts, sortRoot = 'duedate', startFieldId = '' }) {
  const out = [];
  let base = 0;
  // A work item whose only rows under it are its TABLE LINES opens and shuts
  // like any other parent (part.linesOf — see flattenStructure).
  // (a folder that groups its children: a header per value under each row —
  // see splitChildren — with the children's paths under the header's)
  const walkItems = (items, byKey, kidsOf, level, parentPath, onPath, linesOf = null, childGroup = null) => {
    for (const it of items) {
      const k = keyOf(it);
      if (onPath.has(k)) continue;
      const path = `${parentPath}/${k}`;
      const kidKeys = (kidsOf.get(k) || []).filter((ck) => byKey.has(ck));
      if (kidKeys.length || (linesOf && (linesOf(k) || []).length)) {
        out.push({ path, level, rel: level - base, key: k });
        onPath.add(k);
        const { rest, groups } = splitChildren(kidKeys.map((ck) => byKey.get(ck)), childGroup);
        walkItems(rest, byKey, kidsOf, level + 1, path, onPath, linesOf, childGroup);
        groups.forEach((g) => {
          const gp = `${path}/c:${g.key}`;
          out.push({ path: gp, level: level + 1, rel: level + 1 - base, key: gp });
          walkItems(g.items, byKey, kidsOf, level + 2, gp, onPath, linesOf, childGroup);
        });
        onPath.delete(k);
      }
    }
  };
  const walkGroups = (items, part, depth, level, parentPath) => {
    const { byKey, kidsOf, groupsOf } = part;
    const groups = part.groups || part.folder.groups || [];
    if (depth >= groups.length) { walkItems(items, byKey, kidsOf, level, parentPath, new Set(), part.linesOf || null, part.childGroup || null); return; }
    bracket(items, groupsOf, depth).forEach((g) => {
      const path = `${parentPath}/g:${groups[depth].id}:${g.key}`;
      out.push({ path, level, rel: level - base, key: path });
      walkGroups(g.items, part, depth + 1, level + 1, path);
    });
  };
  const tree = folderTree(parts);
  const walkFolder = (part, level, parentPath) => {
    const path = parentPath ? `${parentPath}/f:${part.folder.id}` : `f:${part.folder.id}`;
    // A bare folder has no row of its own (see flattenStructure): its
    // contents sit at its level and count from it as if it were there.
    const bare = !!part.folder.bare;
    if (!bare) out.push({ path, level, rel: 0, key: path, isFolder: true });
    const inner = bare ? level : level + 1;
    (tree.childrenOf.get(part.folder.id) || []).forEach((child) => walkFolder(child, inner, path));
    base = inner - 1;
    walkGroups(sortItems(part.roots || [], sortRoot, startFieldId), part, 0, inner, path);
  };
  tree.roots.forEach((part) => walkFolder(part, 0, ''));
  return out;
}

// ---- what the structure filter actually means --------------------------------
// TWO READINGS, and the difference is not academic — it was worth exactly 298
// work items on this client's structure.
//
//   prune           an item the filter drops is not carried, and nothing is
//                   reached THROUGH it. Cheap: the filter folds into every
//                   query, so the network never carries what will be thrown
//                   away.
//   keep-ancestors  the filter decides what is SHOWN, not what is reached.
//                   An item it drops is still kept when something under it
//                   matches, because otherwise the match would vanish with
//                   its parent.
//
// The structure this page replaces uses keep-ancestors, and here is the proof
// that settled it. Under one project, `status not in (ARCHIVE, TRASH)` drops
// JS2RES2023-22 (ARCHIVE) — and with it the only route to JS2RES2023-426 and
// -436, two Done sub-tasks that the filter is perfectly happy with. Pruning
// gives 629 work items; the structure shows 632; 629 + those three = 632.
//
// keep-ancestors costs a second pass: the tree is walked WITHOUT the filter,
// and the items the filter rejects are found separately — by asking for the
// filter's complement, which is normally tiny (305 rows out of 90 000 here).
// `excludedZqlFor` builds that complement; a page that cannot afford it, or
// whose complement turns out to be enormous, falls back to prune and says so.
export const FILTER_MODES = [
  { id: 'keep-ancestors', label: 'Keep a row when anything under it matches (a structure’s own rule)' },
  { id: 'prune', label: 'Drop a row the filter rejects, and everything under it' },
];

export const excludedZqlFor = (filterZql) => {
  const f = String(filterZql || '').trim();
  return f ? `NOT (${f})` : '';
};

// ---- a work item leaves the tree ---------------------------------------------
// DELETING A WORK ITEM USED TO RELOAD THE STRUCTURE. The screen said "deleted",
// the page said "I no longer know what I hold" and walked twenty-nine
// thousand rows again, minutes, with the reader's open folders gone. The
// item is simply taken out: from the folder's index, from its roots, from
// every parent's child list, and its own child list with it. Anything it
// alone led to is unreachable and is left to fall out of the rows.
export function withoutItem(part, key) {
  if (!part || !key || !part.byKey || !part.byKey.has(key)) return part;
  const byKey = new Map(part.byKey);
  byKey.delete(key);
  const kidsOf = new Map();
  part.kidsOf.forEach((kids, pk) => {
    if (pk === key) return;
    const rest = kids.filter((ck) => ck !== key);
    if (rest.length) kidsOf.set(pk, rest);
  });
  return { ...part, byKey, roots: (part.roots || []).filter((r) => keyOf(r) !== key), kidsOf };
}

// ---- the one filter ----------------------------------------------------------
// A row survives if it matches, or if anything under it does — dropping a
// project because its own summary did not match would hide the match. The
// answer is a pruned { roots, kidsOf } per folder, which every other
// function here takes unchanged.
export function pruneToMatches({ roots, byKey, kidsOf }, hit) {
  const parentsOf = new Map();
  kidsOf.forEach((kids, pk) => kids.forEach((ck) => {
    if (!parentsOf.has(ck)) parentsOf.set(ck, []);
    parentsOf.get(ck).push(pk);
  }));
  const keep = new Set();
  const up = (k, seen = new Set()) => {
    if (keep.has(k) || seen.has(k)) return;
    seen.add(k);
    keep.add(k);
    (parentsOf.get(k) || []).forEach((pk) => up(pk, seen));
  };
  hit.forEach((k) => { if (byKey.has(k)) up(k); });
  const pruned = new Map();
  keep.forEach((k) => {
    const kids = (kidsOf.get(k) || []).filter((ck) => keep.has(ck));
    if (kids.length) pruned.set(k, kids);
  });
  const prunedByKey = new Map();
  keep.forEach((k) => { if (byKey.has(k)) prunedByKey.set(k, byKey.get(k)); });
  return {
    roots: (roots || []).filter((r) => keep.has(keyOf(r))),
    byKey: prunedByKey,
    kidsOf: pruned,
    kept: keep.size,
  };
}

// THE INSERTED ROWS ONLY. A quick button that narrows the folder's INSERT
// (buttonsScope 'roots'): the roots that pass `test`, and EVERYTHING that
// hangs under them, whether it passes or not — the way a structure reads a
// query AND-ed into its insert. A root that fails takes its branch with it.
export function keepRoots({ roots, byKey, kidsOf }, test) {
  const kept = (roots || []).filter((r) => test(keyOf(r)));
  const keep = new Set();
  const stack = kept.map(keyOf);
  while (stack.length) {
    const k = stack.pop();
    if (keep.has(k)) continue;
    keep.add(k);
    (kidsOf.get(k) || []).forEach((c) => stack.push(c));
  }
  const kids = new Map();
  keep.forEach((k) => { const ks = kidsOf.get(k); if (ks && ks.length) kids.set(k, ks); });
  const by = new Map();
  keep.forEach((k) => { if (byKey.has(k)) by.set(k, byKey.get(k)); });
  return { roots: kept, byKey: by, kidsOf: kids, kept: keep.size };
}

// ---- the quick buttons ---------------------------------------------------------
// The buttons switched ON, as one key test: several on join by the grid's
// `mode` ('all' AND, 'any' OR) and narrow what its `scope` says ('tree' every
// row and what leads to it, 'roots' the inserted rows and all under them).
// `setOf(zql)` is the page's answer store — undefined / 'loading' while the
// server answers, { keys } when it has, { error } when it failed (a failed
// button narrows nothing). Answers { pending, roots, rows }: the test for the
// way the grid narrows, null for the other way (and when nothing narrows).
//
// MODE MATTERS: with ANY, a "Hide Archive" button switched on beside
// "Approved projects" lets every non-archived row of every project back in
// (measured on a live grid: 390 inserted rows where the approved projects
// hold 228). Buttons that are each meant to narrow want ALL.
export function buttonTests(buttons, { mode = 'all', scope = 'tree', setOf }) {
  const on = (buttons || []).filter((b) => b && b.zql && String(b.zql).trim());
  const answer = (b) => setOf(String(b.zql).trim());
  if (on.some((b) => { const s = answer(b); return !s || s === 'loading'; })) return { pending: true, roots: null, rows: null };
  const sets = on.map((b) => { const s = answer(b); return s && s.keys ? s.keys : null; }).filter(Boolean);
  const test = sets.length ? (k) => { const n = sets.filter((s) => s.has(k)).length; return mode === 'any' ? n > 0 : n === sets.length; } : null;
  return { pending: false, roots: scope === 'roots' ? test : null, rows: scope === 'roots' ? null : test };
}
